/**
 * Capa de impresión del POS (V146): rutea cada documento por ROL a la impresora
 * configurada EN EL SERVIDOR (qz silencioso / red ePOS / navegador), con cache corto.
 * NUNCA bloquea ni falla en silencio: si el camino silencioso no salió, cae al navegador
 * y devuelve el motivo para que el operador se entere (ver PrintOutcome.usedFallback).
 *
 * El panel de configuración por estación de antes se reemplaza por la pantalla de
 * Impresoras (servidor, por sucursal, con roles y prueba). PrinterSettingsModal la abre.
 */
import { X } from 'lucide-react';
import { fetchPrinters } from '../printing/config';
import { printDocument, openPrintWindow, PrintOutcome } from '../printing/router';
import { PrintersSettings } from '../printing/PrintersSettings';
import { DocKind, PaperWidth, Printer } from '../printing/types';

export type { PrintOutcome } from '../printing/router';
export { openPrintWindow };

// --- Cache corto del inventario de impresoras (evita refetch por cada impresión) ---
let cache: { key: string; at: number; printers: Printer[] } | null = null;
async function getPrintersCached(branchId?: string | null): Promise<Printer[]> {
  const key = branchId ?? '';
  if (cache && cache.key === key && Date.now() - cache.at < 30000) return cache.printers;
  const printers = await fetchPrinters(branchId).catch(() => [] as Printer[]);
  cache = { key, at: Date.now(), printers };
  return printers;
}
export function invalidatePrinterCache() { cache = null; }

/** V180 — expone la lista de impresoras (cacheada) para decidir si imprimir la
 *  comanda: sin impresora de cocina, "Enviar a cocina" NO abre diálogo. */
export async function getPrintersFor(branchId?: string | null): Promise<Printer[]> {
  return getPrintersCached(branchId);
}

/**
 * Imprime un documento: rutea por rol a la impresora del servidor. `payload` = ESC/POS
 * (para qz/red); `html(width)` = HTML térmico por ancho (para navegador/fallback).
 */
export async function printTicket(opts: {
  tenantId?: string;
  branchId?: string | null;
  kind: DocKind;
  escpos: (w: PaperWidth) => string;
  html: (w: PaperWidth) => string;
}): Promise<PrintOutcome> {
  const printers = await getPrintersCached(opts.branchId);
  return printDocument({ printers, branchId: opts.branchId, kind: opts.kind, escpos: opts.escpos, html: opts.html });
}

/** Recibo HTML genérico 80mm (legacy; los documentos nuevos usan src/printing/ticket.ts). */
export function receiptHtml(title: string, bodyHtml: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
    <style>@page{size:80mm auto;margin:0}*{font-family:ui-monospace,"Courier New",monospace}
      body{width:80mm;margin:0;padding:2.5mm 3mm;color:#000}</style></head><body>${bodyHtml}
    <script>window.onload=function(){setTimeout(function(){window.print()},60)}</script></body></html>`;
}

// ===========================================================================
// Configuración de impresoras — abre la pantalla de servidor (por sucursal, roles).
// ===========================================================================
export function PrinterSettingsModal({ branchId = null, onClose }:
  { tenantId?: string; branchId?: string | null; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-slate-900/40 p-4" onClick={onClose}>
      <div className="mx-auto my-6 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex justify-end">
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <PrintersSettings branchId={branchId} />
      </div>
    </div>
  );
}
